import React from 'react';
import { Autocomplete, Button, Typography, Grid, Stack } from '@mui/material';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux'
import CssTextField from '../../../overrides/longSearchField';
import CloseButton from '../../../overrides/closeButton';
import { getTodayInputDate } from '../../../../utils/common';
import { DROPDOWN_PLACEHOLDER_ID } from '../../../../utils/constants';
import { saveCutoffLunchMenuWatcher } from '../../../../redux/actionCreators/admin/cutoffLunchMenu';

const menuNameOf = (row) => {
    const name = Array.isArray(row?.cMenuName) ? row.cMenuName[0] : row?.cMenuName
    return `${name || ''}`.trim().toLowerCase()
}

function CutoffLunchMenuCreation(props) {
    const dispatch = useDispatch()

    const { setOpen, editData } = props;

    const {
        EmployeeReducers: { vendorName },
        MenuItemMasterReducers: { menuItemMasterList },
        MenuMasterReducers: { menuMasterList, mealDropdownList }
    } = useSelector((state) => state)

    const vendorOptions = vendorName || []

    // Same "Select Type" as the menu item master; decides whose login shows the cutoff
    const typeOptions = ((mealDropdownList.length && mealDropdownList[2]) || []).filter((t) => +t.id !== DROPDOWN_PLACEHOLDER_ID)

    const today = getTodayInputDate();

    const CutoffLunchMenuSchema = Yup.object().shape({
        cutoffDate: Yup.string()
            .required('Date is required')
            .oneOf([today], 'Only the current date is allowed'),
        startTime: Yup.string()
            .required('Start time is required'),
        endTime: Yup.string()
            .required('End time is required')
            .test('after-start', 'End time must be after start time', function (value) {
                const { startTime } = this.parent
                if (!startTime || !value) return true
                return value > startTime
            }),
        lunchCount: Yup.number()
            .typeError('Lunch count must be a number')
            .integer('Lunch count must be a whole number')
            .min(1, 'Lunch count must be greater than zero')
            .required('Lunch count is required'),
        userType: Yup.number()
            .min(1, 'Type is required')
            .required('Type is required'),
        vendorId: Yup.number()
            .min(1, 'Vendor is required')
            .required('Vendor is required'),
        menuItemId: Yup.number()
            .min(1, 'Menu item is required')
            .required('Menu item is required'),
    });

    const formik = useFormik({
        initialValues: {
            cutoffDate: today,
            startTime: editData?.StartTime || '',
            endTime: editData?.EndTime || '',
            lunchCount: editData?.LunchCount ?? '',
            userType: editData?.UserType || '',
            vendorId: editData?.VendorId || '',
            menuItemId: editData?.MenuItemId || '',
            menuItemName: editData?.MenuItemName || '',
            menuId: editData?.MenuId || '',
        },

        validationSchema: CutoffLunchMenuSchema,

        onSubmit: () => {
            const { cutoffDate, startTime, endTime, lunchCount, userType, vendorId, menuItemId, menuItemName, menuId } = formik.values

            dispatch(saveCutoffLunchMenuWatcher({
                ACTION: editData ? 'UPDATE' : 'INSERT',
                NID: editData?.nID,
                CUTOFFDATE: cutoffDate,
                STARTTIME: startTime,
                ENDTIME: endTime,
                LUNCHCOUNT: +lunchCount,
                MENUITEMID: +menuItemId,
                MENUITEMNAME: menuItemName,
                MENUID: +menuId || null,
                VENDORIDS: `${vendorId}`,
                USERTYPE: +userType
            }, setOpen))
        }
    });

    const { errors, touched, handleSubmit, getFieldProps, setFieldValue, values } = formik;

    const selectedType = typeOptions.find((t) => +t.id === +values.userType) || null
    const selectedVendor = vendorOptions.find((v) => +v.id === +values.vendorId) || null

    const menuNamesOfVendor = (vendor) => new Set(
        (menuMasterList || [])
            .filter((m) => `${m.cVendorName || ''}`.trim().toLowerCase() === `${vendor?.name || ''}`.trim().toLowerCase())
            .map(menuNameOf)
    )

    // Items of the vendor's menus that are set up for the chosen type
    const itemsFor = (vendor, type) => {
        if (!vendor || !type) return []
        const menus = menuNamesOfVendor(vendor)
        return (menuItemMasterList || [])
            .filter((m) => menus.has(menuNameOf(m)) && +m.typeID === +type)
    }

    const menuItemOptions = itemsFor(selectedVendor, values.userType)

    const selectedMenuItem = menuItemOptions.find((m) => +m.nID === +values.menuItemId) || null

    // The picked item may not belong to the new vendor / type any more
    const keepMenuItemIfValid = (vendor, type) => {
        if (itemsFor(vendor, type).some((m) => +m.nID === +values.menuItemId)) return
        setFieldValue("menuItemId", '')
        setFieldValue("menuItemName", '')
        setFieldValue("menuId", '')
    }

    const handleTypeChange = (value) => {
        setFieldValue("userType", value ? value.id : '')
        keepMenuItemIfValid(selectedVendor, value?.id)
    }

    const handleVendorChange = (value) => {
        setFieldValue("vendorId", value ? value.id : '')
        keepMenuItemIfValid(value, values.userType)
    }

    return (
        <>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <Typography variant='h5' align='left' style={{ paddingBottom: '10px' }}>
                        {editData ? 'Cutoff Lunch Menu Update' : 'Cutoff Lunch Menu Creation'}
                    </Typography>

                    <Grid container style={{ paddingTop: '10px' }} spacing={3} direction='row'>
                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <CssTextField
                                required={true}
                                fullWidth={true}
                                label='Date'
                                name='cutoffDate'
                                color='primary'
                                type='date'
                                variant='outlined'
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: today, max: today, readOnly: true }}
                                {...getFieldProps("cutoffDate")}
                                error={Boolean(touched.cutoffDate && errors.cutoffDate)}
                                helperText={touched.cutoffDate && errors.cutoffDate}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <CssTextField
                                required={true}
                                fullWidth={true}
                                label='Start Time'
                                name='startTime'
                                color='primary'
                                type='time'
                                variant='outlined'
                                InputLabelProps={{ shrink: true }}
                                {...getFieldProps("startTime")}
                                error={Boolean(touched.startTime && errors.startTime)}
                                helperText={touched.startTime && errors.startTime}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <CssTextField
                                required={true}
                                fullWidth={true}
                                label='End Time'
                                name='endTime'
                                color='primary'
                                type='time'
                                variant='outlined'
                                InputLabelProps={{ shrink: true }}
                                {...getFieldProps("endTime")}
                                error={Boolean(touched.endTime && errors.endTime)}
                                helperText={touched.endTime && errors.endTime}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <CssTextField
                                required={true}
                                fullWidth={true}
                                placeholder='Lunch Count'
                                label='Lunch Count'
                                name='lunchCount'
                                color='primary'
                                type='number'
                                variant='outlined'
                                inputProps={{ min: 1 }}
                                {...getFieldProps("lunchCount")}
                                error={Boolean(touched.lunchCount && errors.lunchCount)}
                                helperText={touched.lunchCount && errors.lunchCount}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <Autocomplete
                                fullWidth
                                options={typeOptions}
                                value={selectedType}
                                size="medium"
                                autoHighlight
                                isOptionEqualToValue={(option, value) => +option.id === +value.id}
                                onChange={(event, value) => handleTypeChange(value)}
                                getOptionLabel={(option) => option.name || ''}
                                renderInput={(params) => <CssTextField {...params}
                                    label='Select Type'
                                    name='userType'
                                    required={true}
                                    error={Boolean(touched.userType && errors.userType)}
                                    helperText={touched.userType && errors.userType}
                                />}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <Autocomplete
                                fullWidth
                                options={vendorOptions}
                                value={selectedVendor}
                                size="medium"
                                autoHighlight
                                isOptionEqualToValue={(option, value) => +option.id === +value.id}
                                onChange={(event, value) => handleVendorChange(value)}
                                getOptionLabel={(option) => option.name || ''}
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.id}>
                                        {option.name}
                                    </li>
                                )}
                                renderInput={(params) => <CssTextField {...params}
                                    label='Vendor'
                                    name='vendorId'
                                    required={true}
                                    error={Boolean(touched.vendorId && errors.vendorId)}
                                    helperText={touched.vendorId && errors.vendorId}
                                />}
                            />
                        </Grid>

                        <Grid lg={6} md={6} sm={6} xs={12} item={true}>
                            <Autocomplete
                                fullWidth
                                disabled={!selectedVendor || !selectedType}
                                options={menuItemOptions}
                                value={selectedMenuItem}
                                size="medium"
                                autoHighlight
                                isOptionEqualToValue={(option, value) => +option.nID === +value.nID}
                                onChange={(event, value) => {
                                    setFieldValue("menuItemId", value ? value.nID : '')
                                    setFieldValue("menuItemName", value ? value.cItemName : '')
                                    setFieldValue("menuId", value ? value.nMenuID : '')
                                }}
                                getOptionLabel={(option) => option.cItemName || ''}
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.nID}>
                                        {option.cItemName}
                                    </li>
                                )}
                                renderInput={(params) => <CssTextField {...params}
                                    label='Menu Item'
                                    name='menuItemId'
                                    required={true}
                                    error={Boolean(touched.menuItemId && errors.menuItemId)}
                                    helperText={
                                        (touched.menuItemId && errors.menuItemId)
                                        || (!selectedType ? 'Select a type first' : '')
                                        || (!selectedVendor ? 'Select a vendor first' : '')
                                    }
                                />}
                            />
                        </Grid>

                        <Grid xs={12} item={true}>
                            <Stack spacing={2} direction='row' justifyContent='end' >
                                <CloseButton onClick={() => setOpen(false)}
                                    sx={{
                                        height: '42px',
                                        left: '0px',
                                        top: '0px',
                                        minWidth: '120px',
                                        marginTop: 'auto',
                                    }}
                                    variant='contained'
                                    size='medium'
                                    type='button'>
                                    <Typography variant='t14_400' color='common.black'>Cancel</Typography>
                                </CloseButton>
                                <Button onClick={handleSubmit}
                                    style={{
                                        height: '42px',
                                        left: '0px',
                                        top: '0px',
                                        minWidth: '120px',
                                        backgroundColor: 'common.Bblack',
                                        marginTop: 'auto'
                                    }}
                                    variant='contained'
                                    size='medium'
                                    type='submit'
                                >
                                    <Typography variant='t14_400' color='grey.0'>Submit</Typography>
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Form>
            </FormikProvider>
        </>
    );
}

export default CutoffLunchMenuCreation;
